import type * as Types from '@trello/graphql/generated';

import type { tag } from '@trello/utility-types';
import { useQuickLoad } from '@trello/quickload';
import type { TrelloQueryHookOptions, TrelloLazyQueryHookOptions, TrelloSuspenseQueryHookOptions } from '@trello/quickload';
import type { TypedDocumentNode as DocumentNode } from '@graphql-typed-document-node/core';
import * as Apollo from '@apollo/client';
export type __OpaqueBrandRef = typeof tag;
export const TrelloProposedEventsDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"TrelloProposedEvents"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"nodeId"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"ID"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"trello"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"member"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"id"},"value":{"kind":"Variable","name":{"kind":"Name","value":"nodeId"}}}],"directives":[{"kind":"Directive","name":{"kind":"Name","value":"optIn"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"to"},"value":{"kind":"StringValue","value":"TrelloMember","block":false}}]}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"planner"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"proposedEvents"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"edges"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"node"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"cards"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"edges"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"node"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"name"}}]}}]}}]}},{"kind":"Field","name":{"kind":"Name","value":"endTime"}},{"kind":"Field","name":{"kind":"Name","value":"objectId"}},{"kind":"Field","name":{"kind":"Name","value":"startTime"}}]}}]}}]}}]}}]}}]}}]}}]} as unknown as DocumentNode<TrelloProposedEventsQuery, TrelloProposedEventsQueryVariables>;
const defaultOptions = {"context":{"operationName":"TrelloProposedEvents","document":TrelloProposedEventsDocument}} as const;
export type TrelloProposedEventsQueryVariables = Types.Exact<{
  nodeId: Types.Scalars['ID']['input'];
}>;


export type TrelloProposedEventsQuery = { trello: (
    { __typename: 'TrelloQueryApi' }
    & { member?: Types.Maybe<(
      { __typename: 'TrelloMember' }
      & Pick<Types.TrelloMember, 'id'>
      & { planner?: Types.Maybe<(
        { __typename: 'TrelloPlanner' }
        & Pick<Types.TrelloPlanner, 'id'>
        & { proposedEvents?: Types.Maybe<(
          { __typename: 'TrelloPlannerProposedEventConnection' }
          & { edges?: Types.Maybe<Array<(
            { __typename: 'TrelloPlannerProposedEventEdge' }
            & { node?: Types.Maybe<(
              { __typename: 'TrelloProposedEvent' }
              & Pick<Types.TrelloProposedEvent, 'endTime' | 'objectId' | 'startTime'>
              & { cards?: Types.Maybe<(
                { __typename: 'TrelloProposedEventCardConnection' }
                & { edges?: Types.Maybe<Array<(
                  { __typename: 'TrelloProposedEventCardEdge' }
                  & { node?: Types.Maybe<
                    | (
                      { __typename: 'TrelloCard' }
                      & Pick<Types.TrelloCard, 'id' | 'name'>
                    )
                    | (
                      { __typename: 'TrelloInboxCard' }
                      & Pick<Types.TrelloInboxCard, 'id' | 'name'>
                    )
                  > }
                )>> }
              )> }
            )> }
          )>> }
        )> }
      )> }
    )> }
  ) };

/**
 * __useTrelloProposedEventsQuery__
 *
 * To run a query within a React component, call `useTrelloProposedEventsQuery` and pass it any options that fit your needs.
 * When your component renders, `useTrelloProposedEventsQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = useTrelloProposedEventsQuery({
 *   variables: {
 *      nodeId: // value for 'nodeId'
 *   },
 * });
 */
export function useTrelloProposedEventsQuery(
  baseOptions: TrelloQueryHookOptions<
    TrelloProposedEventsQuery,
    TrelloProposedEventsQueryVariables
  > &
    (
      | { variables: TrelloProposedEventsQueryVariables; skip?: boolean }
      | { skip: boolean }
    ),
) {
  const isQuickLoading = useQuickLoad({
    waitOn: baseOptions?.waitOn || ['None'],
    document: TrelloProposedEventsDocument,
    skip: baseOptions?.skip,
  });
  const options = {
    ...defaultOptions,
    ...baseOptions,
    skip: isQuickLoading || baseOptions?.skip,
  };
  const result = Apollo.useQuery<
    TrelloProposedEventsQuery,
    TrelloProposedEventsQueryVariables
  >(TrelloProposedEventsDocument, options);
  // reconstruct result because modifying the useQuery result actually changes apollo behavior because of memoization
  const trelloResult = { ...result };
  if (isQuickLoading) {
    trelloResult.loading = true;
  }

  return trelloResult;
}
export function useTrelloProposedEventsLazyQuery(
  baseOptions?: TrelloLazyQueryHookOptions<
    TrelloProposedEventsQuery,
    TrelloProposedEventsQueryVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<
    TrelloProposedEventsQuery,
    TrelloProposedEventsQueryVariables
  >(TrelloProposedEventsDocument, options);
}
export function useTrelloProposedEventsSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | TrelloSuspenseQueryHookOptions<
        TrelloProposedEventsQuery,
        TrelloProposedEventsQueryVariables
      >,
) {
  const options =
    baseOptions === Apollo.skipToken
      ? baseOptions
      : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<
    TrelloProposedEventsQuery,
    TrelloProposedEventsQueryVariables
  >(TrelloProposedEventsDocument, options);
}
export type TrelloProposedEventsQueryHookResult = ReturnType<
  typeof useTrelloProposedEventsQuery
>;
export type TrelloProposedEventsLazyQueryHookResult = ReturnType<
  typeof useTrelloProposedEventsLazyQuery
>;
export type TrelloProposedEventsSuspenseQueryHookResult = ReturnType<
  typeof useTrelloProposedEventsSuspenseQuery
>;
export type TrelloProposedEventsQueryResult = Apollo.QueryResult<
  TrelloProposedEventsQuery,
  TrelloProposedEventsQueryVariables
>;
